import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import {
  getDataLayerNumFilters,
  getDataLayerSearchFilters,
  getDataLayerSearchFiltersObject,
} from '@/features/shared/HOCs/withGTM';
import { useIsRevision } from '@/features/shared/Hooks';
import { ColorNames, LoadingStatusValues, Theme } from '@/utils/constants';
import { useMediaQuery } from '@material-ui/core';
import { useTheme, withStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { prop } from 'lodash/fp';
import React, {
  memo,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import TagManager from 'react-gtm-module';

import { BreadcrumbBox } from '@/features/Breadcrumb';
import Image from '@/features/shared/Image';
import {
  MuiCollapse,
  MuiGrid,
  MuiTab,
  MuiTabs,
  MuiTooltip,
  MuiTypography,
} from '@/features/shared/material-ui';
import RelatedLinks from '@/features/shared/RelatedLinks';
import { CaptureClicksRichText } from '@/features/shared/RichText';
import { IntroContentProps } from './IntroContent.interface';
import useStyles from './IntroContent.styles';

const TabIcon = memo(
  ({ classes, categoryIcon }: any) => {
    return (
      <div className={classes.categoryIcon}>
        <Image image={categoryIcon} />
      </div>
    );
  },
  // secure comparison
  (prevProps: any, nextProps: any) =>
    prop(['categoryIcon', 'origin', 'url'], prevProps) ===
    prop(['categoryIcon', 'origin', 'url'], nextProps),
);

const getDeviceType = ({ categoryType = '', categoryTitle = '' }) => {
  const categoryTypes = ['Camera', 'Intercom', 'Stratocast', 'Access Control'];

  return (
    categoryType ||
    categoryTypes
      .find((currentType = '') => currentType === categoryTitle)
      ?.toLowerCase()
      ?.replace(' ', '-')
  );
};

const CategoryTooltip = withStyles(
  (muiTheme) => ({
    tooltip: {
      boxShadow: muiTheme.shadows[2],
      backgroundColor: ColorNames.white,
      padding: muiTheme.spacing(2),
      margin: 0,
    },
  }),
  { name: 'GenSDLIntroContentCategoryTooltip' },
)(MuiTooltip);

const IntroContent = ({
  categoryContentCompound = [],
  collapseDisclaimer,
  description,
  disclaimer,
  labels,
  heading,
  includeBreadcrumb,
  quickLinks,
  children,
  theme,
}: PropsWithChildren<IntroContentProps>) => {
  const [dataLayerId, setDataLayerId] = React.useState('');
  const classes = useStyles({});
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const {
    updateQueryParams,
    queryParams,
    forceUpdate = () => null,
    resetParams = () => null,
    status,
    pagination,
  } = useContext(ElasticComponentContext);
  const [activeIndex, setActiveIndex] = useState<number | null>(0);
  const isRevision = useIsRevision();

  useEffect(() => {
    if (queryParams?.category_type?.length > 0) {
      const ACTIVE = categoryContentCompound.findIndex(
        (item) => queryParams.category_type === getDeviceType(item),
      );
      setActiveIndex(Math.max(ACTIVE, 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams?.category_type]);

  const [showDisclaimer, setShowDisclaimer] = useState(!collapseDisclaimer);
  const handleCategoryClick = useCallback(
    (deviceType = '') => {
      if (!deviceType) return;

      updateQueryParams('category_type', deviceType, false);
      updateQueryParams('page', '1');
      resetParams();
      forceUpdate();
      setDataLayerId('sdl_category_switch');
    },
    [forceUpdate, resetParams, updateQueryParams],
  );

  React.useEffect(() => {
    if (status !== LoadingStatusValues.loading) return;
    if (dataLayerId !== 'sdl_category_switch') return;

    const filters = queryParams?.filters;
    const searchFiltersObject = getDataLayerSearchFiltersObject(filters);
    const searchFilters = getDataLayerSearchFilters(filters);

    const dataLayer = {
      event: 'trackEvent-sdlCategorySwitch',
      event_name: 'sdl_category_switch',
      ...(!!queryParams?.q?.trim() && { search_term: queryParams?.q?.trim() }),
      num_search_result: pagination?.totalResults || 0,
      index_num: String(pagination?.currentPage || 1),
      is_revision: isRevision,
      sdl_category: queryParams?.category_type,
      ...(!!searchFilters && { search_filters: getDataLayerSearchFilters(filters) }),
      num_search_filters: getDataLayerNumFilters(filters),
      ...searchFiltersObject,
    };
    TagManager.dataLayer({ dataLayer });

    setDataLayerId('');
  }, [
    dataLayerId,
    isRevision,
    pagination?.currentPage,
    pagination?.totalResults,
    queryParams?.category_type,
    queryParams?.filters,
    queryParams.page,
    queryParams?.q,
    status,
  ]);

  const handleDisclaimerClick = () => {
    setShowDisclaimer(!showDisclaimer);
  };

  const categories = useMemo(
    () =>
      categoryContentCompound &&
      categoryContentCompound.length &&
      categoryContentCompound.map(
        ({ categoryIcon, categoryDescription, categoryTitle, categoryType }, key) => {
          const categoryKey = `category_${key}`;
          const deviceType = getDeviceType({ categoryType, categoryTitle });

          return (
            // eslint-disable-next-line react/no-array-index-key
            <CategoryTooltip
              onClick={() => {
                handleCategoryClick(deviceType);
              }}
              key={categoryKey}
              title={
                categoryDescription ? (
                  <CaptureClicksRichText theme={theme} content={categoryDescription} />
                ) : (
                  ''
                )
              }
              interactive
              placement="bottom"
            >
              <MuiTab
                className={clsx(classes.categoryTab, 'gtm-category')}
                value={deviceType || ''}
                label={categoryTitle}
                icon={<TabIcon categoryIcon={categoryIcon} classes={classes} />}
              />
            </CategoryTooltip>
          );
        },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categoryContentCompound, theme],
  );

  const rootClasses = clsx(classes.root, 'gntc-intro-content', `${theme}-theme`);
  return (
    <MuiGrid container className={rootClasses}>
      {children}

      <MuiGrid className={classes.introHeader} item xs={12} md={8}>
        {includeBreadcrumb && <BreadcrumbBox className={classes.breadcrumbs} theme={theme} />}
        <MuiTypography variant="h2" className={classes.introHeading}>
          {heading}
        </MuiTypography>

        {description && (
          <CaptureClicksRichText
            theme={Theme.light}
            className={classes.introDescription}
            content={description}
          />
        )}

        {disclaimer && (
          <div className={classes.disclaimerContainer}>
            <button
              type="button"
              className={classes.disclaimerTrigger}
              onClick={handleDisclaimerClick}
            >
              {showDisclaimer ? `${labels?.disclaimer_text} -` : `${labels?.disclaimer_text} +`}
            </button>
            <MuiCollapse in={showDisclaimer}>
              <CaptureClicksRichText
                theme={Theme.light}
                content={disclaimer}
                className={classes.disclaimerInfo}
              />
            </MuiCollapse>
          </div>
        )}
      </MuiGrid>
      <MuiGrid className={classes.quickLinks} item xs={12} md={4}>
        {quickLinks && (
          <RelatedLinks
            className={classes.relatedLinks}
            content={quickLinks}
            theme={isMobile ? theme : Theme.light}
          />
        )}
      </MuiGrid>
      <MuiTabs
        className={classes.categoryTabs}
        value={activeIndex}
        variant="scrollable"
        selectionFollowsFocus
        scrollButtons="on"
        aria-label="scrollable device categories"
      >
        {categories}
      </MuiTabs>
    </MuiGrid>
  );
};

export default memo(IntroContent);
